import React, { useMemo } from 'react';
import { AlertTriangle, Eye, Lightbulb, ArrowRight } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

interface InsightItem {
  type: 'concern' | 'missing-perspective' | 'prompt' | 'recommendation';
  text: string;
  severity: 'critical' | 'important' | 'info';
  avatar?: string;
}

const severityConfig = {
  critical: { bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-400', icon: AlertTriangle },
  important: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', icon: Eye },
  info: { bg: 'bg-primary/10', border: 'border-primary/20', text: 'text-primary', icon: Lightbulb },
};

const AiInsightsFeedWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  const guidance = state?.facilitatorGuidance ?? null;

  const insights: InsightItem[] = useMemo(() => {
    if (!guidance) return [];

    const items: InsightItem[] = [];

    // Missing perspectives
    for (const mp of guidance.missingPerspectives) {
      let severity: InsightItem['severity'];
      if (mp.importance === 'critical') severity = 'critical';
      else if (mp.importance === 'important') severity = 'important';
      else severity = 'info';

      items.push({
        type: 'missing-perspective',
        text: `${mp.avatar}: ${mp.reason}`,
        severity,
        avatar: mp.avatar,
      });
    }

    // Conversation concerns
    for (const concern of guidance.conversationHealth.concerns) {
      items.push({
        type: 'concern',
        text: concern,
        severity: 'important',
      });
    }

    // Suggested prompts
    for (const prompt of guidance.suggestedPrompts) {
      items.push({
        type: 'prompt',
        text: prompt,
        severity: 'info',
      });
    }

    // Next step recommendation
    if (guidance.nextStepRecommendation) {
      items.push({
        type: 'recommendation',
        text: guidance.nextStepRecommendation,
        severity: 'info',
      });
    }

    return items;
  }, [guidance]);

  if (!state) return null;

  if (insights.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        No AI insights yet
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full px-3 py-2 gap-2 overflow-y-auto">
      {insights.map((insight, i) => {
        const config = severityConfig[insight.severity];
        const Icon = insight.type === 'recommendation' ? ArrowRight : config.icon;

        return (
          <div
            key={`${insight.type}-${insight.text.slice(0, 20)}`}
            className={`flex items-start gap-2.5 p-2.5 rounded-lg border ${config.bg} ${config.border}`}
          >
            <Icon size={14} className={`${config.text} shrink-0 mt-0.5`} />
            <div className="min-w-0">
              <span className={`text-xs font-bold uppercase tracking-wider ${config.text}`}>
                {insight.type.replace('-', ' ')}
              </span>
              <p className={`text-xs mt-0.5 leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                {insight.text}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AiInsightsFeedWidget;
